import { Card } from "@tremor/react";
import { Monitor as MonitorIcon, Smartphone } from "lucide-react";
import { useEffect, useState } from "react";
import { fetchMonitorScreenshots } from "../../api/monitors.js";

// Screenshot timeline for `screenshot` monitors: scrub the capture history,
// toggle desktop/mobile, and compare any two captures side by side.

function ViewportToggle({ value, onChange }) {
  const opts = [
    { id: "desktop", label: "Desktop", Icon: MonitorIcon },
    { id: "mobile", label: "Mobile", Icon: Smartphone },
  ];
  return (
    <div className="inline-flex rounded-md border border-gray-200 p-0.5">
      {opts.map(({ id, label, Icon }) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          className={[
            "flex items-center gap-1.5 rounded px-3 py-1 text-sm",
            value === id ? "bg-teal-600 text-white" : "text-gray-600 hover:bg-gray-100",
          ].join(" ")}
        >
          <Icon className="h-3.5 w-3.5" />
          {label}
        </button>
      ))}
    </div>
  );
}

function Thumb({ shot, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "shrink-0 overflow-hidden rounded-md border-2 transition",
        active ? "border-teal-600" : "border-transparent hover:border-gray-300",
      ].join(" ")}
      title={new Date(shot.captured_at).toLocaleString()}
    >
      <img
        src={shot.url}
        alt={shot.captured_at}
        className="h-20 w-32 bg-gray-100 object-cover object-top"
        loading="lazy"
      />
      <span className="block px-1 py-0.5 text-[10px] text-gray-500">
        {new Date(shot.captured_at).toLocaleDateString()}{" "}
        {new Date(shot.captured_at).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}
      </span>
    </button>
  );
}

export default function ScreenshotHistoryPanel({ monitorId }) {
  const [viewport, setViewport] = useState("desktop");
  const [shots, setShots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compare, setCompare] = useState(false);
  const [selA, setSelA] = useState(0);
  const [selB, setSelB] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchMonitorScreenshots(monitorId, { viewport, limit: 60 })
      .then((data) => {
        if (cancelled) return;
        setShots(data ?? []);
        setSelA(0);
        setSelB(data && data.length > 1 ? 1 : 0);
      })
      .catch(() => !cancelled && setShots([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [monitorId, viewport]);

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Screenshot history</h3>
          <p className="text-xs text-gray-400">
            {shots.length} capture{shots.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ViewportToggle value={viewport} onChange={setViewport} />
          {shots.length > 1 && (
            <label className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={compare}
                onChange={(e) => setCompare(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
              />
              Compare
            </label>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center text-sm text-gray-400">
          Loading…
        </div>
      ) : shots.length === 0 ? (
        <div className="flex h-64 items-center justify-center text-sm text-gray-400">
          No captures yet — the first one appears after the monitor runs.
        </div>
      ) : compare ? (
        <div className="grid grid-cols-2 gap-4">
          {[
            [selA, setSelA],
            [selB, setSelB],
          ].map(([sel, setSel], col) => (
            <div key={col}>
              <select
                value={sel}
                onChange={(e) => setSel(Number(e.target.value))}
                className="mb-2 w-full rounded-md border border-gray-300 px-2 py-1 text-xs"
              >
                {shots.map((s, i) => (
                  <option key={s.id} value={i}>
                    {new Date(s.captured_at).toLocaleString()}
                  </option>
                ))}
              </select>
              <img
                src={shots[sel]?.url}
                alt="capture"
                className="w-full rounded border border-gray-200 bg-gray-50"
              />
            </div>
          ))}
        </div>
      ) : (
        <div>
          <img
            src={shots[selA]?.url}
            alt="selected capture"
            className="mb-3 max-h-[600px] w-full rounded border border-gray-200 bg-gray-50 object-contain object-top"
          />
          <div className="flex gap-2 overflow-x-auto pb-1">
            {shots.map((s, i) => (
              <Thumb
                key={s.id}
                shot={s}
                active={i === selA}
                onClick={() => setSelA(i)}
              />
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
